/** @module The design's bar chart: one bar per day, today highlighted, an axis below. */

export interface Bar {
  /** Unique per bar, usually the date. */
  key: string;
  value: number;
  /** Tooltip text, e.g. "05.10.: 4.812,37 €". */
  title: string;
}

/** More bars than this turn into a grey smear on a phone. */
export const MAX_BARS = 60;

/** Every n-th point, always keeping the first and the last. */
export function sample<T>(points: T[], limit = MAX_BARS): T[] {
  if (points.length <= limit) return points;
  const step = (points.length - 1) / (limit - 1);
  const keep = new Set(Array.from({ length: limit }, (_, index) => Math.round(index * step)));
  return points.filter((_, index) => keep.has(index));
}

/** Bar heights in percent; the lowest bar still shows, the highest fills the box. */
export function heights(values: number[]): number[] {
  const low = Math.min(...values);
  const high = Math.max(...values);
  if (high === low) return values.map(() => 60);
  return values.map((value) => 20 + ((value - low) / (high - low)) * 80);
}

export function BarChart({
  bars,
  label,
  axis,
  className = "h-16",
}: {
  bars: Bar[];
  /** What the chart says, for screen readers: "from 4.500 € to 4.812 €". */
  label: string;
  axis: string[];
  className?: string;
}) {
  const sized = heights(bars.map((bar) => bar.value));
  return (
    <div className="flex flex-col gap-1">
      <div className={`bars ${className}`} role="img" aria-label={label}>
        {bars.map((bar, index) => (
          <div
            key={bar.key}
            className={`bar ${index === bars.length - 1 ? "bar-hi" : ""}`}
            style={{ height: `${String(sized[index] ?? 0)}%` }}
            title={bar.title}
          />
        ))}
      </div>
      <div className="axis">
        {axis.map((tick, index) => (
          <span key={`${tick}-${String(index)}`}>{tick}</span>
        ))}
      </div>
    </div>
  );
}
